import { useMemo } from "react";
import truncate from "lodash/truncate";

interface SelectedTextItem {
  label: string;
  value: string | number;
}

interface SelectedTextProps {
  items?: SelectedTextItem[];
  selectedText: string | string[];
}

const toTitleString = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value ?? "";
};

const getSelectedItemIds = (urlText: string | string[]): string[] => {
  if (Array.isArray(urlText)) {
    return urlText;
  }
  return urlText?.split(",") ?? [];
};

export const SelectedText = ({
  selectedText,
  items = [],
}: SelectedTextProps) => {
  const itemMap = useMemo(
    () =>
      items.reduce((acc, item) => {
        acc.set(String(item.value), item.label);
        return acc;
      }, new Map<string, string>()),
    [items],
  );

  const getLabel = (value: string): string => {
    const label = itemMap.get(value);
    return label ?? value;
  };

  const getSelectedText = (urlText: string | string[]) => {
    const selectedItemsIds = getSelectedItemIds(urlText);
    const selectedItems = selectedItemsIds.map(getLabel);
    const truncatedSelectedItems = selectedItems.map((text: string) =>
      truncate(text, { length: 20 }),
    );
    const count = selectedItems.length;
    if (count === 0) {
      return {
        truncatedText: "",
      };
    }

    // Count of items fully shown
    // More items are shown with + count
    const fullyShownCount = 2;
    if (count <= fullyShownCount) {
      return {
        text: selectedItems.join(", "),
        truncatedText: truncatedSelectedItems.join(", "),
      };
    }
    return {
      text: toTitleString(selectedItems),
      truncatedText: `${truncatedSelectedItems
        .slice(0, fullyShownCount)
        .join(", ")} + ${count - 2}`,
    };
  };

  const { text, truncatedText } = getSelectedText(selectedText);
  return <p title={text}>{truncatedText}</p>;
};
